import { useEffect, useRef } from "react";
import {
  buildChatWsUrl,
  fetchChatWsTicket,
  pollChatEvents,
  signalChatPresenceOffline,
  type ChatConversation,
  type ChatMessage,
  type ChatPollEvent,
  type ChatProfile,
  type ChatReaction,
} from "@/lib/chatApi";

export type ChatRealtimeHandlers = {
  onMessage?: (message: ChatMessage) => void;
  onTyping?: (conversationId: number, userId: number, typing: boolean) => void;
  onRead?: (conversationId: number, userId: number, messageId: number) => void;
  /** Snapshot complet online/offline (chaque réponse poll / frame WS). */
  onPresenceSnapshot?: (presence: Record<number, string>, lastSeen?: Record<number, number>) => void;
  onMessageEdited?: (message: ChatMessage) => void;
  onMessageDeleted?: (conversationId: number, messageId: number) => void;
  onMessageReaction?: (
    conversationId: number,
    messageId: number,
    reactions: ChatReaction[],
    meta?: { userId: number; emoji: string | null; action: string }
  ) => void;
  onConversationUpdated?: (conversation: ChatConversation) => void;
  onConversationRemoved?: (conversationId: number) => void;
  onProfileUpdated?: (profile: ChatProfile) => void;
  onCallInvite?: (payload: {
    conversationId: number;
    callId: string;
    room: string;
    media: "audio" | "video";
    fromUserId: number;
    fromName: string;
    memberIds?: number[];
  }) => void;
  onCallAccept?: (conversationId: number, callId: string, userId: number) => void;
  onCallReject?: (conversationId: number, callId: string, userId: number) => void;
  onCallEnd?: (conversationId: number, callId: string, userId: number) => void;
  /** Quelqu’un a rejoint une visio en cours (ou bascule d’appareil). */
  onCallJoin?: (conversationId: number, callId: string, userId: number) => void;
  /** Visio ouverte en groupe (sans sonnerie) — afficher Rejoindre. */
  onCallStarted?: (payload: {
    conversationId: number;
    callId: string;
    room: string;
    media: "audio" | "video";
    fromUserId: number;
    joinable: boolean;
  }) => void;
  onMeetingChanged?: (conversationId: number) => void;
  onAuriz?: (payload: {
    conversationId: number;
    fromUserId: number;
    fromName: string;
    toUserId: number;
  }) => void;
  onEvent?: (event: ChatPollEvent) => void;
};

function parsePresenceSnapshot(raw?: Record<string, string>): Record<number, string> {
  const out: Record<number, string> = {};
  if (!raw) return out;
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k);
    if (!Number.isFinite(id) || id < 1) continue;
    const s = (v || "").toLowerCase();
    if (s === "online" || s === "dnd" || s === "meeting" || s === "incall") out[id] = s;
    else out[id] = "offline";
  }
  return out;
}

function parseLastSeenSnapshot(raw?: Record<string, unknown>): Record<number, number> {
  const out: Record<number, number> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw)) {
    const id = Number(k);
    const unix = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(id) || id < 1 || !Number.isFinite(unix) || unix < 1) continue;
    out[id] = Math.floor(unix);
  }
  return out;
}

function isHubSuspendMessage(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const t = (data as { type?: string }).type;
  return t === "AURELIA_CHAT_FRAME_HIDDEN" || t === "AURELIA_SUITE_LOGOUT";
}

/**
 * Temps réel Chat : WebSocket en priorité, long-poll en fallback.
 * Offline immédiat si onglet / iframe caché, hub hide, ou pagehide.
 */
export function useChatRealtime(
  suiteUserId: string | null,
  enabled: boolean,
  handlers: ChatRealtimeHandlers
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled || !suiteUserId) return;

    let cancelled = false;
    let since = new Date().toISOString();
    let sinceId: number | undefined;
    let bootstrapped = false;
    let ctrl: AbortController | null = null;
    let loopActive = false;
    let mode: "ws" | "poll" | null = null;
    let ws: WebSocket | null = null;
    let pingTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let wsFailCount = 0;

    const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

    const applyEvents = (events: ChatPollEvent[]) => {
      const h = handlersRef.current;
      for (const ev of events) {
        h.onEvent?.(ev);
        if (ev.type === "message" && ev.message && typeof ev.message === "object") {
          h.onMessage?.(ev.message as ChatMessage);
        } else if (ev.type === "message_edited" && ev.message && typeof ev.message === "object") {
          h.onMessageEdited?.(ev.message as ChatMessage);
        } else if (ev.type === "message_deleted") {
          const cid = Number(ev.conversation_id);
          const mid = Number(ev.message_id);
          if (Number.isFinite(cid) && Number.isFinite(mid)) {
            h.onMessageDeleted?.(cid, mid);
          }
        } else if (ev.type === "message_reaction") {
          const cid = Number(ev.conversation_id);
          const mid = Number(ev.message_id);
          const reactions = Array.isArray(ev.reactions) ? (ev.reactions as ChatReaction[]) : [];
          if (Number.isFinite(cid) && Number.isFinite(mid)) {
            const emojiRaw = ev.emoji;
            h.onMessageReaction?.(cid, mid, reactions, {
              userId: Number(ev.user_id) || 0,
              emoji: typeof emojiRaw === "string" ? emojiRaw : null,
              action: typeof ev.action === "string" ? ev.action : "",
            });
          }
        } else if (ev.type === "profile_updated" && ev.profile && typeof ev.profile === "object") {
          h.onProfileUpdated?.(ev.profile as ChatProfile);
        } else if (ev.type === "conversation_updated" && ev.conversation) {
          h.onConversationUpdated?.(ev.conversation as ChatConversation);
        } else if (ev.type === "conversation_deleted") {
          const cid = Number(ev.conversation_id);
          if (Number.isFinite(cid)) h.onConversationRemoved?.(cid);
        } else if (ev.type === "conversation_left") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const self = Number(suiteUserId);
          if (Number.isFinite(cid) && Number.isFinite(uid) && uid === self) {
            h.onConversationRemoved?.(cid);
          }
        } else if (ev.type === "typing") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          if (Number.isFinite(cid) && Number.isFinite(uid)) {
            h.onTyping?.(cid, uid, Boolean(ev.typing));
          }
        } else if (ev.type === "read") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const mid = Number(ev.message_id);
          if (Number.isFinite(cid) && Number.isFinite(uid) && Number.isFinite(mid)) {
            h.onRead?.(cid, uid, mid);
          }
        } else if (ev.type === "call_invite") {
          const cid = Number(ev.conversation_id);
          const fromUserId = Number(ev.from_user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          const room = typeof ev.room === "string" ? ev.room : "";
          if (Number.isFinite(cid) && Number.isFinite(fromUserId) && callId) {
            h.onCallInvite?.({
              conversationId: cid,
              callId,
              room,
              media: ev.media === "audio" ? "audio" : "video",
              fromUserId,
              fromName: typeof ev.from_name === "string" ? ev.from_name : "Appel",
              memberIds: Array.isArray(ev.member_ids)
                ? (ev.member_ids as unknown[]).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
                : undefined,
            });
          }
        } else if (ev.type === "call_accept") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          if (Number.isFinite(cid) && Number.isFinite(uid) && callId) {
            h.onCallAccept?.(cid, callId, uid);
          }
        } else if (ev.type === "call_reject") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          if (Number.isFinite(cid) && Number.isFinite(uid) && callId) {
            h.onCallReject?.(cid, callId, uid);
          }
        } else if (ev.type === "call_end") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          if (Number.isFinite(cid) && Number.isFinite(uid) && callId) {
            h.onCallEnd?.(cid, callId, uid);
          }
        } else if (ev.type === "call_join") {
          const cid = Number(ev.conversation_id);
          const uid = Number(ev.user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          if (Number.isFinite(cid) && Number.isFinite(uid) && callId) {
            h.onCallJoin?.(cid, callId, uid);
          }
        } else if (ev.type === "call_started") {
          const cid = Number(ev.conversation_id);
          const fromUserId = Number(ev.from_user_id);
          const callId = typeof ev.call_id === "string" ? ev.call_id : "";
          const room = typeof ev.room === "string" ? ev.room : "";
          if (Number.isFinite(cid) && Number.isFinite(fromUserId) && callId) {
            const self = Number(suiteUserId);
            const openJoin = ev.open_join === true || ev.open_join === 1;
            const partIds = Array.isArray(ev.participant_ids)
              ? (ev.participant_ids as unknown[]).map((x) => Number(x)).filter((n) => Number.isFinite(n))
              : [];
            h.onCallStarted?.({
              conversationId: cid,
              callId,
              room,
              media: ev.media === "audio" ? "audio" : "video",
              fromUserId,
              joinable: openJoin || partIds.includes(self),
            });
          }
        } else if (ev.type === "meeting") {
          const cid = Number(ev.conversation_id);
          if (Number.isFinite(cid) && cid > 0) h.onMeetingChanged?.(cid);
        } else if (ev.type === "auriz") {
          const cid = Number(ev.conversation_id);
          const fromUserId = Number(ev.from_user_id);
          const toUserId = Number(ev.to_user_id);
          if (Number.isFinite(cid) && Number.isFinite(fromUserId) && Number.isFinite(toUserId)) {
            h.onAuriz?.({
              conversationId: cid,
              fromUserId,
              fromName: typeof ev.from_name === "string" ? ev.from_name : "Quelqu’un",
              toUserId,
            });
          }
        }
      }
    };

    const clearWsTimers = () => {
      if (pingTimer != null) {
        window.clearInterval(pingTimer);
        pingTimer = null;
      }
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const stopWs = () => {
      clearWsTimers();
      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        } catch {
          /* ignore */
        }
        ws = null;
      }
      if (mode === "ws") mode = null;
    };

    const stopPoll = (markOffline: boolean) => {
      loopActive = false;
      ctrl?.abort();
      ctrl = null;
      if (markOffline) {
        signalChatPresenceOffline(suiteUserId);
      }
    };

    const stopAll = (markOffline: boolean) => {
      stopWs();
      stopPoll(markOffline);
    };

    const handleRealtimePayload = (data: Record<string, unknown>) => {
      if (data.type === "pong") return;
      if (data.success === false) return;
      const presenceRaw = data.presence;
      if (presenceRaw && typeof presenceRaw === "object" && !Array.isArray(presenceRaw)) {
        const lastSeenRaw = data.last_seen;
        handlersRef.current.onPresenceSnapshot?.(
          parsePresenceSnapshot(presenceRaw as Record<string, string>),
          lastSeenRaw && typeof lastSeenRaw === "object" && !Array.isArray(lastSeenRaw)
            ? parseLastSeenSnapshot(lastSeenRaw as Record<string, unknown>)
            : undefined
        );
      }
      if (data.bootstrap === true) {
        bootstrapped = true;
        if (typeof data.since === "string") since = data.since;
        if (typeof data.since_id === "number") sinceId = data.since_id;
        return;
      }
      const events = Array.isArray(data.events) ? (data.events as ChatPollEvent[]) : [];
      applyEvents(events);
      if (typeof data.since === "string") since = data.since;
      if (typeof data.since_id === "number") sinceId = data.since_id;
      bootstrapped = true;
    };

    const startPollLoop = async () => {
      if (loopActive || cancelled) return;
      mode = "poll";
      loopActive = true;
      while (!cancelled && loopActive) {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          stopPoll(true);
          break;
        }
        ctrl = new AbortController();
        const r = await pollChatEvents(suiteUserId, {
          since: bootstrapped ? since : undefined,
          since_id: bootstrapped ? sinceId : undefined,
          bootstrap: !bootstrapped,
          signal: ctrl.signal,
        });
        if (cancelled || !loopActive) break;
        if (r.success && r.data) {
          handleRealtimePayload({
            success: true,
            events: r.data.events,
            since: r.data.since,
            since_id: r.data.since_id,
            presence: r.data.presence,
            last_seen: r.data.last_seen,
            bootstrap: !bootstrapped ? true : undefined,
          });
          // bootstrap branch above sets bootstrapped; for poll first response:
          if (!bootstrapped && r.data) {
            bootstrapped = true;
            if (r.data.since) since = r.data.since;
            if (typeof r.data.since_id === "number") sinceId = r.data.since_id;
          }
          await sleep(200);
          continue;
        }
        if (r.error === "aborted") break;
        await sleep(2500);
      }
      loopActive = false;
    };

    const scheduleWsReconnect = () => {
      if (cancelled || reconnectTimer != null) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const delay = Math.min(8000, 800 * Math.max(1, wsFailCount));
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        void startWs();
      }, delay);
    };

    const startWs = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        signalChatPresenceOffline(suiteUserId);
        return;
      }
      stopWs();
      mode = "ws";
      const ticketRes = await fetchChatWsTicket(suiteUserId);
      if (cancelled) return;
      if (!ticketRes.success || !ticketRes.data) {
        wsFailCount += 1;
        if (wsFailCount >= 2) {
          void startPollLoop();
          return;
        }
        scheduleWsReconnect();
        return;
      }
      const url = buildChatWsUrl(ticketRes.data.ticket, ticketRes.data.ws_path);
      if (!url) {
        wsFailCount += 1;
        void startPollLoop();
        return;
      }

      try {
        ws = new WebSocket(url);
      } catch {
        wsFailCount += 1;
        if (wsFailCount >= 2) {
          void startPollLoop();
          return;
        }
        scheduleWsReconnect();
        return;
      }

      ws.onopen = () => {
        wsFailCount = 0;
        pingTimer = window.setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: "ping" }));
            } catch {
              /* ignore */
            }
          }
        }, 20000);
      };

      ws.onmessage = (ev) => {
        let data: Record<string, unknown>;
        try {
          data = JSON.parse(String(ev.data)) as Record<string, unknown>;
        } catch {
          return;
        }
        handleRealtimePayload(data);
      };

      ws.onerror = () => {
        /* onclose gère le repli */
      };

      ws.onclose = () => {
        clearWsTimers();
        ws = null;
        if (cancelled) return;
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          signalChatPresenceOffline(suiteUserId);
          return;
        }
        wsFailCount += 1;
        if (wsFailCount >= 3) {
          void startPollLoop();
          return;
        }
        scheduleWsReconnect();
      };
    };

    const onVis = () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") {
        stopAll(true);
        return;
      }
      wsFailCount = 0;
      bootstrapped = false;
      void startWs();
    };

    const onPageHide = () => {
      if (cancelled) return;
      stopAll(true);
    };

    const onHubMsg = (event: MessageEvent) => {
      if (cancelled) return;
      if (!isHubSuspendMessage(event.data)) return;
      stopAll(true);
    };

    if (document.visibilityState === "visible") {
      void startWs();
    } else {
      signalChatPresenceOffline(suiteUserId);
    }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("message", onHubMsg);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("message", onHubMsg);
      stopAll(true);
    };
  }, [suiteUserId, enabled]);
}
